"use client"
import React, { useEffect, useState } from 'react'
import { motion, AnimatePresence } from "motion/react"
import { BlurFade } from '@/app/components/BlurFade'
import { MMHeader, MMTabBar, MMFooter } from './MMChrome'
import { RedirectToPC } from '@/app/components/DeviceRedirect'
import { pick, type Locale } from '@/lib/i18n'
import { mmDict } from '@/lib/i18n/dicts/mm'
import { commonDict } from '@/lib/i18n/dicts/common'
import { categoryLabel, gameCover, type LocalizedGame } from '@/lib/game-i18n'
import type { Category } from '@/lib/categories'

// 封面：后台上传的列表图（/media/）优先，否则按中文名取 gamelistpic2 默认封面（lib/game-i18n.gameCover）
const coverFor = gameCover

const sceneIcons = [
    (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6">
            <path strokeLinecap="round" strokeLinejoin="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z" />
        </svg>
    ),
    (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 0 0 6-6v-1.5m-6 7.5a6 6 0 0 1-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 0 1-3-3V4.5a3 3 0 1 1 6 0v8.25a3 3 0 0 1-3 3Z" />
        </svg>
    ),
    (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
        </svg>
    ),
]

export interface MMHomeProps {
    games: LocalizedGame[]
    // 热门游戏：服务端按「推荐优先、无推荐回退 sort_order 前 8」选好后传入
    hotGames: LocalizedGame[]
    categories: Category[]
    // 数据背书读库统计：游戏数 / 分类数
    counts: { games: number; categories: number }
    locale: Locale
}

export default function MMHome({ games, hotGames, categories, counts, locale }: MMHomeProps) {
    const [sloganIdx, setSloganIdx] = useState(0)
    const t = pick(mmDict, locale).home
    const common = pick(commonDict, locale)
    const slogans = t.slogans
    const catLabel = (name: string) =>
        categoryLabel(categories.find((c) => c.name === name) ?? { name, name_en: '' }, locale)

    // 标语自动轮播
    useEffect(() => {
        const t = setInterval(() => setSloganIdx((i) => (i + 1) % slogans.length), 4000)
        return () => clearInterval(t)
    }, [])

    const marqueeGames = games.slice(0, 10)

    return (
        <div className="bg-slate-950 min-h-screen text-slate-200 max-w-md mx-auto relative overflow-x-hidden shadow-2xl shadow-black">
            <style>{`
                @keyframes mm-marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
                @keyframes mm-float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
            `}</style>

            {/* ============ 顶部导航 ============ */}
            <MMHeader locale={locale} />
            <RedirectToPC href="/" />

            {/* ============ Hero ============ */}
            <section className="relative px-5 pt-10 pb-8 overflow-hidden">
                {/* 背景图（游戏氛围，底部渐隐融入底色） */}
                <div className="pointer-events-none absolute inset-0">
                    <img src="/img/mm/hero-bg.png" alt="" className="h-full w-full object-cover opacity-80" />
                    <div className="absolute inset-0 bg-gradient-to-b from-slate-950/20 via-slate-950/45 to-slate-950" />
                </div>
                <div className="pointer-events-none absolute -top-24 left-1/2 h-64 w-64 -translate-x-1/2 rounded-full bg-indigo-600/25 blur-[90px]" />
                <div className="pointer-events-none absolute top-32 -right-16 h-48 w-48 rounded-full bg-fuchsia-500/15 blur-[80px]" />

                {/* 内容层：relative z-10 确保文字始终渲染在背景遮罩之上，
                    否则入场动画结束（filter/opacity 还原）后静态文字会被 absolute 遮罩压暗 */}
                <div className="relative z-10">
                <BlurFade delay={0.05} inView>
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-indigo-500/10 ring-1 ring-indigo-400/30 px-3 py-1 text-[11px] font-medium text-indigo-300">
                        <span className="relative flex size-1.5">
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-indigo-400 opacity-75"></span>
                            <span className="relative inline-flex size-1.5 rounded-full bg-indigo-400"></span>
                        </span>
                        {t.badge}
                    </span>
                </BlurFade>

                {/* 轮播标语 */}
                <div className="mt-5 min-h-[10.5rem]">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={sloganIdx}
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -24 }}
                            transition={{ duration: 0.45, ease: "easeOut" }}
                        >
                            <h1 className="text-4xl font-black leading-tight text-transparent bg-clip-text bg-gradient-to-r from-indigo-300 via-white to-fuchsia-300">
                                {slogans[sloganIdx].title}
                            </h1>
                            <p className="mt-3 text-sm font-semibold text-slate-200">
                                {slogans[sloganIdx].sub}
                            </p>
                            <p className="mt-2 text-xs leading-5 text-slate-400">
                                {slogans[sloganIdx].desc}
                            </p>
                        </motion.div>
                    </AnimatePresence>
                </div>

                {/* 轮播指示点 */}
                <div className="mt-2 flex gap-1.5">
                    {slogans.map((_, i) => (
                        <button
                            key={i}
                            onClick={() => setSloganIdx(i)}
                            className={`h-1.5 rounded-full transition-all duration-300 ${i === sloganIdx ? "w-8 bg-gradient-to-r from-indigo-400 to-fuchsia-400" : "w-3 bg-white/20"}`}
                        />
                    ))}
                </div>

                {/* 双 CTA：主按钮 免费获取方案 → /mm/contact，次按钮 查看全部游戏 → /mm/gamelist */}
                <BlurFade delay={0.25} inView>
                    <div className="mt-6 flex gap-3">
                        <a href="/mm/contact"
                            className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-gradient-to-r from-indigo-500 to-fuchsia-500 px-5 py-3 text-sm font-bold text-white shadow-lg shadow-indigo-600/30 active:scale-95 transition-transform animate-breathe">
                            {t.ctaPrimary}
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path fill="currentColor" d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" /></svg>
                        </a>
                        <a href="/mm/gamelist"
                            className="flex-1 inline-flex items-center justify-center rounded-full ring-1 ring-white/20 px-5 py-3 text-sm font-medium text-slate-200 active:scale-95 transition-transform">
                            {t.ctaSecondary}
                        </a>
                    </div>
                </BlurFade>
                </div>
            </section>

            {/* ============ 封面跑马灯 ============ */}
            <section className="py-4 overflow-hidden">
                <div className="flex w-max gap-3 px-4" style={{ animation: "mm-marquee 28s linear infinite" }}>
                    {[...marqueeGames, ...marqueeGames].map((g, i) => (
                        <a key={`${g.gameid}-${i}`} href={`/mm/gamedetail/${g.gameid}`} className="w-28 shrink-0 block">
                            <div className="aspect-[4/3] rounded-xl overflow-hidden ring-1 ring-white/10 active:ring-indigo-400/60 transition-shadow">
                                <img src={coverFor(g)} alt={g.name} loading="lazy"
                                    onError={(e) => { e.currentTarget.src = g.imageUrl }}
                                    className="h-full w-full object-cover" />
                            </div>
                            <p className="mt-1.5 text-center text-[11px] text-slate-400 truncate">{g.name}</p>
                        </a>
                    ))}
                </div>
            </section>

            {/* ============ 数据背书（游戏数/分类数读库，语种/节点为固定文案；移动端直接显示，不做滚动动画） ============ */}
            <section className="px-5 py-6">
                <div className="grid grid-cols-2 gap-3">
                    {[
                        { num: `${counts.games}${t.stats.games.suffix}`, label: t.stats.games.label },
                        { num: `${counts.categories}${t.stats.categories.suffix}`, label: t.stats.categories.label },
                        { num: t.stats.languages.value, label: t.stats.languages.label },
                        { num: t.stats.nodes.value, label: t.stats.nodes.label },
                    ].map((s, i) => (
                        <BlurFade key={s.label} delay={0.08 * i} inView>
                            <div className="rounded-2xl bg-slate-900 ring-1 ring-white/10 p-4 text-center">
                                <div className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-indigo-300 to-fuchsia-300">
                                    {s.num}
                                </div>
                                <div className="mt-1 text-[11px] text-slate-400">{s.label}</div>
                            </div>
                        </BlurFade>
                    ))}
                </div>
            </section>

            {/* ============ 热门游戏 ============ */}
            <section className="px-5 py-6">
                <BlurFade inView>
                    <div className="flex items-end justify-between">
                        <div>
                            <p className="text-xs font-semibold tracking-widest text-indigo-400 uppercase">{t.hot.eyebrow}</p>
                            <h2 className="mt-1 text-2xl font-black text-white">{t.hot.title}</h2>
                        </div>
                        <a href="/mm/gamelist" className="text-xs text-indigo-400 flex items-center gap-0.5">
                            {t.hot.all}
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="size-3.5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
                            </svg>
                        </a>
                    </div>
                </BlurFade>

                <div className="mt-5 flex flex-col gap-4">
                    {hotGames.map((g, i) => (
                        <BlurFade key={g.gameid} delay={0.06 * i} inView>
                            <a href={`/mm/gamedetail/${g.gameid}`}
                                className="group block rounded-2xl bg-slate-900 ring-1 ring-white/10 overflow-hidden active:scale-[0.98] transition-transform">
                                <div className="relative aspect-[4/3] overflow-hidden">
                                    <img src={coverFor(g)} alt={g.name} loading="lazy"
                                        onError={(e) => { e.currentTarget.src = g.imageUrl }}
                                        className="h-full w-full object-cover" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-transparent" />
                                    <span className="absolute top-3 left-3 rounded-full bg-indigo-500/80 backdrop-blur px-2.5 py-0.5 text-[10px] font-medium text-white">
                                        {catLabel(g.catelog)}
                                    </span>
                                    <div className="absolute bottom-3 left-4 right-4 flex items-end justify-between">
                                        <h3 className="text-lg font-bold text-white">{g.name}</h3>
                                        <span className="inline-flex items-center gap-1 rounded-full bg-white/10 backdrop-blur px-2.5 py-1 text-[10px] text-white ring-1 ring-white/20">
                                            {common.game.viewDetail}
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="size-3">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                                            </svg>
                                        </span>
                                    </div>
                                </div>
                                <p className="px-4 py-3 text-xs leading-5 text-slate-400 line-clamp-2">
                                    {g.一句话描述 || g.游戏简介}
                                </p>
                            </a>
                        </BlurFade>
                    ))}
                </div>
            </section>

            {/* ============ 全场景互动赋能 ============ */}
            <section className="px-5 py-6">
                <BlurFade inView>
                    <p className="text-xs font-semibold tracking-widest text-indigo-400 uppercase text-center">{t.scenes.eyebrow}</p>
                    <h2 className="mt-1 text-2xl font-black text-white text-center">{t.scenes.title}</h2>
                </BlurFade>
                <div className="mt-5 flex flex-col gap-3">
                    {t.scenes.items.map((s, i) => (
                        <BlurFade key={s.title} delay={0.08 * i} inView>
                            <div className="flex items-start gap-4 rounded-2xl bg-slate-900 ring-1 ring-white/10 p-4">
                                <div className="shrink-0 rounded-xl bg-gradient-to-br from-indigo-500/20 to-fuchsia-500/20 ring-1 ring-indigo-400/30 p-3 text-indigo-300"
                                    style={{ animation: `mm-float 3.5s ease-in-out ${i * 0.5}s infinite` }}>
                                    {sceneIcons[i]}
                                </div>
                                <div>
                                    <h3 className="text-sm font-bold text-white">{s.title}</h3>
                                    <p className="mt-1 text-xs leading-5 text-slate-400">{s.desc}</p>
                                </div>
                            </div>
                        </BlurFade>
                    ))}
                </div>
            </section>

            {/* ============ 全球部署（零动画静态轻量版） ============ */}
            <section className="px-5 py-8">
                <p className="text-xs font-semibold tracking-widest text-indigo-400 uppercase text-center">{t.global.eyebrow}</p>
                <h2 className="mt-1 text-2xl font-black text-white text-center">{t.global.title}</h2>
                <p className="mt-2 text-xs leading-5 text-slate-400 text-center">
                    {t.global.desc}
                </p>

                {/* 静态数据 */}
                <div className="mt-5 grid grid-cols-3 gap-3">
                    {[
                        { value: "150+", label: t.global.stats.countries },
                        { value: "2,800+", label: t.global.stats.nodes },
                        { value: "-60%", label: t.global.stats.latency },
                    ].map((s) => (
                        <div key={s.label} className="rounded-2xl bg-slate-900 ring-1 ring-white/10 p-3.5 text-center">
                            <div className="text-lg font-black text-transparent bg-clip-text bg-gradient-to-r from-indigo-300 to-fuchsia-300">
                                {s.value}
                            </div>
                            <div className="mt-1 text-[10px] text-slate-400">{s.label}</div>
                        </div>
                    ))}
                </div>

                {/* 节点标签云（静态） */}
                <div className="mt-4 flex flex-wrap justify-center gap-2">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-indigo-500 to-fuchsia-500 px-3 py-1.5 text-[11px] font-bold text-white">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="size-3">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
                        </svg>
                        {t.global.hub}
                    </span>
                    {t.global.regions.map((r) => (
                        <span key={r} className="inline-flex items-center gap-1.5 rounded-full bg-white/5 ring-1 ring-white/10 px-3 py-1.5 text-[11px] text-slate-300">
                            <span className="size-1.5 rounded-full bg-emerald-400"></span>
                            {r}
                        </span>
                    ))}
                </div>
            </section>

            {/* ============ 底部 CTA ============ */}
            <section className="px-5 pb-6 pt-2">
                <BlurFade inView>
                    <div className="rounded-3xl bg-gradient-to-r from-indigo-600 to-fuchsia-600 p-6 text-center shadow-xl shadow-indigo-600/25">
                        <h2 className="text-xl font-black text-white">{t.cta.title}</h2>
                        <p className="mt-2 text-xs text-indigo-100">{t.cta.desc}</p>
                        <a href="/mm/contact"
                            className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-3 text-sm font-bold text-indigo-600 active:scale-95 transition-transform">
                            {t.cta.button}
                        </a>
                    </div>
                </BlurFade>
            </section>

            {/* ============ 精简页脚（导航 + 备案/统计，TabBar 之上） ============ */}
            <MMFooter locale={locale} />

            {/* ============ 底部 Tab 栏 ============ */}
            <MMTabBar active="home" locale={locale} />
        </div>
    )
}
